import { Calculator, TrendingUp } from "lucide-react";
import { useMemo, useState } from "react";

import { useCurrentUser } from "@/api/hooks";

/** Cost / revenue / margin modeler for AI pass-through pricing.
 *
 * Tweak per-walkthrough/quote token assumptions, monthly volumes,
 * and markup % — see what the org would owe at scale, what we'd
 * pay Gemini, and the margin. Pure browser-side math; no API.
 *
 * Default token assumptions are sourced from current PencilOut
 * field testing — refine as we get more data.
 *
 * Pencilout-admin only — gated at the route boundary in App.tsx.
 */

// Per-call token defaults (calibrated to ~early 2026 walkthroughs).
const DEFAULT_TOKENS = {
  walkthrough_input: 25_000,
  walkthrough_output: 1_500,
  quote_generate_input: 8_000,
  quote_generate_output: 2_500,
  quote_suggest_input: 5_000,
  quote_suggest_output: 1_200,
};

// Pricing version 2026-01 (mirrors services/ai_pricing.py V_2026_01).
const RATES_FLASH_MICROS_PER_TOKEN = { input: 30, output: 250 };
const RATES_PRO_MICROS_PER_TOKEN = { input: 125, output: 1000 };

interface CalcInputs {
  walkthroughs_per_month: number;
  quotes_per_month: number;
  suggests_per_month: number;
  walkthrough_input: number;
  walkthrough_output: number;
  quote_generate_input: number;
  quote_generate_output: number;
  quote_suggest_input: number;
  quote_suggest_output: number;
  markup_pct: number; // fraction (0.20 = 20%)
  use_pro: boolean;
}

interface CalcOutputs {
  raw_micros: number;
  raw_dollars: number;
  billed_micros: number;
  billed_dollars: number;
  margin_dollars: number;
  margin_pct: number;
  per_walkthrough_billed: number;
  per_quote_billed: number;
  per_suggest_billed: number;
}

function compute(inputs: CalcInputs): CalcOutputs {
  const rates = inputs.use_pro ? RATES_PRO_MICROS_PER_TOKEN : RATES_FLASH_MICROS_PER_TOKEN;

  const walkthrough_micros =
    inputs.walkthrough_input * rates.input + inputs.walkthrough_output * rates.output;
  const quote_micros =
    inputs.quote_generate_input * rates.input + inputs.quote_generate_output * rates.output;
  const suggest_micros =
    inputs.quote_suggest_input * rates.input + inputs.quote_suggest_output * rates.output;

  const total_raw_micros =
    inputs.walkthroughs_per_month * walkthrough_micros +
    inputs.quotes_per_month * quote_micros +
    inputs.suggests_per_month * suggest_micros;

  const total_billed_micros = Math.round(total_raw_micros * (1 + Math.max(0, inputs.markup_pct)));

  const raw_dollars = total_raw_micros / 100_000_000; // µ¢ → ¢ → $
  const billed_dollars = total_billed_micros / 100_000_000;
  const margin_dollars = billed_dollars - raw_dollars;

  return {
    raw_micros: total_raw_micros,
    raw_dollars,
    billed_micros: total_billed_micros,
    billed_dollars,
    margin_dollars,
    margin_pct: raw_dollars > 0 ? (margin_dollars / raw_dollars) * 100 : 0,
    per_walkthrough_billed: (walkthrough_micros * (1 + inputs.markup_pct)) / 100_000_000,
    per_quote_billed: (quote_micros * (1 + inputs.markup_pct)) / 100_000_000,
    per_suggest_billed: (suggest_micros * (1 + inputs.markup_pct)) / 100_000_000,
  };
}

const SCENARIOS: Array<{ label: string; multiplier: number }> = [
  { label: "1×", multiplier: 1 },
  { label: "5×", multiplier: 5 },
  { label: "25×", multiplier: 25 },
  { label: "100×", multiplier: 100 },
];

export function PricingCalculatorPage() {
  const me = useCurrentUser();
  const isAdmin = me.data?.role === "pencilout_admin";

  const [inputs, setInputs] = useState<CalcInputs>({
    walkthroughs_per_month: 20,
    quotes_per_month: 20,
    suggests_per_month: 10,
    ...DEFAULT_TOKENS,
    markup_pct: 0.5, // 50% — round number to model
    use_pro: false,
  });

  const out = useMemo(() => compute(inputs), [inputs]);
  const scenarios = useMemo(
    () =>
      SCENARIOS.map((s) => ({
        ...s,
        out: compute({
          ...inputs,
          walkthroughs_per_month: inputs.walkthroughs_per_month * s.multiplier,
          quotes_per_month: inputs.quotes_per_month * s.multiplier,
          suggests_per_month: inputs.suggests_per_month * s.multiplier,
        }),
      })),
    [inputs],
  );

  if (!isAdmin) {
    return <p className="text-sm text-graphite-soft">This page is for PencilOut staff.</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-mono text-2xl font-bold tracking-tight">Pricing Calculator</h1>
        <p className="mt-1 max-w-3xl text-sm text-graphite-soft">
          Model AI pass-through cost and revenue at different volumes + markups. Token counts are
          per-call estimates — billable = raw × (1 + markup), rounded once at the boundary. Numbers
          are projections, not invoices.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_2fr]">
        {/* ---- Inputs ---- */}
        <div className="space-y-5 rounded-lg border border-rule bg-paper-warm p-5">
          <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
            Inputs
          </h2>

          <NumField
            label="Markup %"
            help="How much PencilOut adds on top of pass-through."
            value={inputs.markup_pct * 100}
            min={0}
            max={1000}
            step={5}
            onChange={(v) => setInputs((s) => ({ ...s, markup_pct: v / 100 }))}
            suffix="%"
          />

          <div className="space-y-1">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={inputs.use_pro}
                onChange={(e) => setInputs((s) => ({ ...s, use_pro: e.target.checked }))}
                className="h-4 w-4"
              />
              <span>Use Gemini 2.5 Pro (4× the cost of Flash)</span>
            </label>
          </div>

          <hr className="border-rule" />

          <h3 className="text-xs font-semibold uppercase tracking-wider text-graphite-soft">
            Volume / month (per org)
          </h3>
          <NumField
            label="Walkthroughs"
            value={inputs.walkthroughs_per_month}
            min={0}
            step={1}
            onChange={(v) => setInputs((s) => ({ ...s, walkthroughs_per_month: v }))}
          />
          <NumField
            label="Quote generations"
            value={inputs.quotes_per_month}
            min={0}
            step={1}
            onChange={(v) => setInputs((s) => ({ ...s, quotes_per_month: v }))}
          />
          <NumField
            label="Suggestion calls"
            value={inputs.suggests_per_month}
            min={0}
            step={1}
            onChange={(v) => setInputs((s) => ({ ...s, suggests_per_month: v }))}
          />

          <hr className="border-rule" />

          <details className="text-sm">
            <summary className="cursor-pointer font-mono text-xs uppercase tracking-wider text-graphite-soft">
              Token assumptions (advanced)
            </summary>
            <div className="mt-3 space-y-3">
              <NumField
                label="Walkthrough input tok"
                value={inputs.walkthrough_input}
                min={0}
                step={1000}
                onChange={(v) => setInputs((s) => ({ ...s, walkthrough_input: v }))}
              />
              <NumField
                label="Walkthrough output tok"
                value={inputs.walkthrough_output}
                min={0}
                step={100}
                onChange={(v) => setInputs((s) => ({ ...s, walkthrough_output: v }))}
              />
              <NumField
                label="Quote-gen input tok"
                value={inputs.quote_generate_input}
                min={0}
                step={500}
                onChange={(v) => setInputs((s) => ({ ...s, quote_generate_input: v }))}
              />
              <NumField
                label="Quote-gen output tok"
                value={inputs.quote_generate_output}
                min={0}
                step={500}
                onChange={(v) => setInputs((s) => ({ ...s, quote_generate_output: v }))}
              />
              <NumField
                label="Suggest input tok"
                value={inputs.quote_suggest_input}
                min={0}
                step={500}
                onChange={(v) => setInputs((s) => ({ ...s, quote_suggest_input: v }))}
              />
              <NumField
                label="Suggest output tok"
                value={inputs.quote_suggest_output}
                min={0}
                step={100}
                onChange={(v) => setInputs((s) => ({ ...s, quote_suggest_output: v }))}
              />
            </div>
          </details>
        </div>

        {/* ---- Outputs ---- */}
        <div className="space-y-6">
          <div className="rounded-lg border border-rule bg-pencil-yellow-soft p-5">
            <div className="flex items-start gap-4">
              <Calculator className="h-10 w-10 flex-none text-graphite" aria-hidden />
              <div className="grid flex-1 grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-4">
                <BigStat label="Pass-through cost" value={`$${out.raw_dollars.toFixed(2)}`} muted />
                <BigStat label="Billed (with markup)" value={`$${out.billed_dollars.toFixed(2)}`} />
                <BigStat label="Margin" value={`$${out.margin_dollars.toFixed(2)}`} />
                <BigStat label="Margin %" value={`${out.margin_pct.toFixed(1)}%`} />
              </div>
            </div>
            <p className="mt-3 font-mono text-xs text-graphite-soft">
              Per-call billed: walkthrough ${out.per_walkthrough_billed.toFixed(3)}, quote $
              {out.per_quote_billed.toFixed(3)}, suggest ${out.per_suggest_billed.toFixed(3)}
            </p>
          </div>

          {/* ---- Scenarios ---- */}
          <div className="rounded-lg border border-rule bg-paper-warm p-5">
            <div className="mb-4 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-graphite-soft" aria-hidden />
              <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
                Volume scaling — at this markup
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-graphite-soft">
                  <tr>
                    <th className="py-2 text-left">Volume</th>
                    <th className="py-2 text-right">Walks/mo</th>
                    <th className="py-2 text-right">Quotes/mo</th>
                    <th className="py-2 text-right">Pass-through</th>
                    <th className="py-2 text-right">Billed</th>
                    <th className="py-2 text-right">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {scenarios.map((s) => (
                    <tr key={s.label} className="border-t border-rule">
                      <td className="py-2 font-mono">{s.label}</td>
                      <td className="py-2 text-right font-mono tabular-nums">
                        {(inputs.walkthroughs_per_month * s.multiplier).toLocaleString()}
                      </td>
                      <td className="py-2 text-right font-mono tabular-nums">
                        {(inputs.quotes_per_month * s.multiplier).toLocaleString()}
                      </td>
                      <td className="py-2 text-right font-mono tabular-nums text-graphite-soft">
                        ${s.out.raw_dollars.toFixed(2)}
                      </td>
                      <td className="py-2 text-right font-mono tabular-nums">
                        <strong>${s.out.billed_dollars.toFixed(2)}</strong>
                      </td>
                      <td className="py-2 text-right font-mono tabular-nums">
                        ${s.out.margin_dollars.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 font-mono text-xs text-graphite-soft">
              Multipliers scale all three volume inputs proportionally. Margin %, per-call costs,
              and rate stay fixed.
            </p>
          </div>

          {/* ---- Markup sensitivity ---- */}
          <MarkupSensitivity inputs={inputs} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------

function MarkupSensitivity({ inputs }: { inputs: CalcInputs }) {
  const markups = [0, 0.1, 0.25, 0.5, 0.75, 1.0, 1.5, 2.0];
  const rows = markups.map((m) => ({
    markup: m,
    out: compute({ ...inputs, markup_pct: m }),
  }));
  return (
    <div className="rounded-lg border border-rule bg-paper-warm p-5">
      <h2 className="mb-4 font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
        Markup sensitivity — at current volume
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-graphite-soft">
            <tr>
              <th className="py-2 text-left">Markup</th>
              <th className="py-2 text-right">Pass-through</th>
              <th className="py-2 text-right">Billed</th>
              <th className="py-2 text-right">Margin / mo</th>
              <th className="py-2 text-right">Per walkthrough</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ markup, out }) => (
              <tr key={markup} className="border-t border-rule">
                <td className="py-2 font-mono tabular-nums">{(markup * 100).toFixed(0)}%</td>
                <td className="py-2 text-right font-mono tabular-nums text-graphite-soft">
                  ${out.raw_dollars.toFixed(2)}
                </td>
                <td className="py-2 text-right font-mono tabular-nums">
                  <strong>${out.billed_dollars.toFixed(2)}</strong>
                </td>
                <td className="py-2 text-right font-mono tabular-nums">
                  ${out.margin_dollars.toFixed(2)}
                </td>
                <td className="py-2 text-right font-mono tabular-nums">
                  ${out.per_walkthrough_billed.toFixed(3)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NumField(props: {
  label: string;
  help?: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium uppercase tracking-wider text-graphite-soft">
        {props.label}
      </span>
      <div className="mt-1 flex items-center gap-2">
        <input
          type="number"
          value={props.value}
          min={props.min}
          max={props.max}
          step={props.step}
          onChange={(e) => props.onChange(Number(e.target.value))}
          className="w-full rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm tabular-nums focus:border-graphite focus:outline-none"
        />
        {props.suffix && (
          <span className="font-mono text-sm text-graphite-soft">{props.suffix}</span>
        )}
      </div>
      {props.help && <p className="mt-1 text-xs text-graphite-soft">{props.help}</p>}
    </label>
  );
}

function BigStat(props: { label: string; value: string; muted?: boolean }) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-graphite-soft">
        {props.label}
      </h3>
      <p
        className={`mt-1 font-mono text-2xl font-bold tabular-nums ${
          props.muted ? "text-graphite-soft" : "text-graphite"
        }`}
      >
        {props.value}
      </p>
    </div>
  );
}
